import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button } from './ui';

/**
 * Keeps one broken screen from taking the whole dashboard with it.
 *
 * Without this, a render error anywhere unmounts the entire tree and leaves a
 * white page — no navigation, no way back, and nothing on screen saying what
 * happened. A staff tool that can lose itself that completely is worse than one
 * that admits a page is broken and leaves the rest usable.
 *
 * It is not a substitute for handling the data properly: it is the net under
 * the case nobody predicted.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The stack is worth having in development; nothing is sent anywhere.
    console.error('[admin] render failed', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return <CrashPanel error={this.state.error} onReset={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

function CrashPanel({ error, onReset }: { error: Error; onReset: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <Alert tone="danger">
        <p className="font-semibold">{t('app.crashed')}</p>
        <p>{t('app.crashedHint')}</p>
      </Alert>

      {/* The message is developer text, so it stays LTR and monospaced rather
          than pretending to be prose in the reader's language. */}
      <pre
        dir="ltr"
        className="dio-input"
        style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap' }}
      >
        {error.message}
      </pre>

      <div className="flex gap-2">
        <Button variant="primary" onClick={onReset}>
          {t('common.retry')}
        </Button>
        <Button onClick={() => window.location.reload()}>{t('common.reload')}</Button>
      </div>
    </div>
  );
}
